import { inject, Injectable } from '@angular/core';
import { PlaybackService } from './playback.service';
import { Observable, take, tap } from 'rxjs';
import { SessionsManagerService } from '../resources/sessions-manager.service';
import { SteeringPath, SteeringWheelPaths } from '../models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PSNotificationTypes } from '@hidat/huijs-player';
import { SteeringMode } from '@splixer/common';

@Injectable({ providedIn: 'root' })
export class SteeringWheelService {
  protected playback = inject(PlaybackService);
  protected session = inject(SessionsManagerService);
  protected shuffleCount = 0;

  protected scheduledPaths: SteeringPath[] = [];
  protected relatedPaths: SteeringPath[] = [];
  protected randomPaths: SteeringPath[] = [];
  protected detourPaths: SteeringPath[] = [];

  constructor() {
    this.playback.notifier.pipe(takeUntilDestroyed()).subscribe((pbN) => {
      if (pbN.type === PSNotificationTypes.SCHEDULING_ITEM_CHANGED) {
        this.resetSteeringWheel();
      }
    });
  }

  protected resetSteeringWheel() {
    this.shuffleCount = 0;
    this.relatedPaths = [];
    this.scheduledPaths = [];
    this.randomPaths = [];
    this.detourPaths = [];
  }

  /**
   * Fetches alternative content paths from the server and caches them into the four path buckets
   * (scheduled, related, random, detours). Call this before reading individual paths.
   * @param steeringMode - Governs which types of paths the server returns.
   * @returns The full `SteeringWheelPaths` response, or `undefined` on error.
   */
  public getPaths(
    steeringMode: SteeringMode,
  ): Observable<SteeringWheelPaths | undefined> {
    //console.debug('Fetching  Alt Paths');
    return this.playback.getAltPaths(steeringMode, this.shuffleCount).pipe(
      tap((paths) => {
        if (paths) {
          if (paths.scheduled) {
            this.scheduledPaths = paths.scheduled;
          } else {
            this.scheduledPaths = [];
          }
          if (paths.random) {
            this.randomPaths = paths.random;
          } else {
            this.randomPaths = [];
          }
          if (paths.related) {
            this.relatedPaths = paths.related;
          } else {
            this.relatedPaths = [];
          }
          if (paths.detours) {
            this.detourPaths = paths.detours;
          } else {
            this.detourPaths = [];
          }
        }
        return paths;
      }),
    );
  }

  /**
   * Returns `true` when the combined scheduled + related + random cache has fewer than 3 entries.
   * The UI should call `getPaths()` to refresh before offering direction choices.
   */
  public needsPaths(): boolean {
    if (
      this.scheduledPaths.length +
        this.relatedPaths.length +
        this.randomPaths.length >=
      3
    ) {
      return false;
    }
    return true;
  }

  /**
   * Returns and removes the next "straight ahead" path. Increments `shuffleCount`.
   *
   * Priority: scheduled → related → random.
   * In `UpNext` mode, related/random are only offered after the first shuffle (`shuffleCount > 1`).
   *
   * @param steeringMode - Controls whether related/random are surfaced on the first press.
   * @returns The next path, or `undefined` if nothing is available for the current mode.
   */
  public getStraightPath(steeringMode: SteeringMode): SteeringPath | undefined {
    this.shuffleCount++;
    console.debug('Fetching straight path: ', this.shuffleCount);
    if (this.scheduledPaths.length > 0) {
      return this.scheduledPaths.shift();
    }
    if (steeringMode !== SteeringMode.UpNext || this.shuffleCount > 1) {
      if (this.relatedPaths.length > 0) {
        return this.relatedPaths.shift();
      }
      if (this.randomPaths.length > 0) {
        return this.randomPaths.shift();
      }
    }
    return undefined;
  }

  /**
   * Returns and removes the next random path (left-turn direction).
   * Returns `undefined` if no random paths are cached — call `getPaths()` to refresh.
   */
  public getLeftPath(): SteeringPath | undefined {
    if (this.randomPaths.length > 0) {
      return this.randomPaths.shift();
    }
    return undefined;
  }

  /**
   * Returns and removes the next related path (right-turn direction).
   * Falls back to a random path if no related paths remain.
   * Returns `undefined` if both buckets are empty.
   */
  public getRightPath(): SteeringPath | undefined {
    if (this.relatedPaths.length > 0) {
      return this.relatedPaths.shift();
    }
    if (this.randomPaths.length > 0) {
      return this.randomPaths.shift();
    }
    return undefined;
  }

  /**
   * Immediately starts playing the given path.
   * Routes to `playSchedulingItem()` for `ScheduleLog` paths; `playMix()` for all others.
   * @param path - The path selected by the user from the steering wheel UI.
   */
  public skipToPath(path: SteeringPath) {
    if (path.uid) {
      if (path.klass === 'ScheduleLog') {
        this.playback.playScheduledItem(path.uid).pipe(take(1)).subscribe();
      } else {
        this.playback
          .playMix(path.uid, 'steering_wheel')
          .pipe(take(1))
          .subscribe();
      }
    }
  }

  /**
   * Returns a context-sensitive label for the steering wheel trigger button.
   * In `UpNext` mode the first press shows "Change Directions"; subsequent presses show "More options".
   * All other modes always show "More options".
   * @param mode - The current steering mode.
   */
  public steeringButtonTitle(mode: SteeringMode): string {
    let name = 'Up Next';
    switch (mode) {
      case SteeringMode.UpNext:
        if (this.shuffleCount > 1) {
          name = 'More options';
        } else {
          name = 'Change Directions';
        }
        break;
      case SteeringMode.StartingPath:
      case SteeringMode.ChangeDirection:
      case SteeringMode.ChooseNextMix:
        name = 'More options';
        break;
    }
    return name;
  }
}
